var __makeTemplateObject = this && this.__makeTemplateObject || function (cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
};
var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var __read = this && this.__read || function (o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
};
var __spreadArray = this && this.__spreadArray || function (to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, ginni } from '@shapeable/utils';
import { useComponentProps } from '../context/component-props-context';
import { OptionMenu } from './option-menu';
import { includes, keyBy, omit, size, debounce } from 'lodash';
import { flattenMenu } from './utils/flatten-menu';
import { useTransition } from "@react-spring/web";
import { useOptionMenuPosition } from './hooks/use-option-menu-position';
import { useInfiniteScrollArray } from '../hooks/use-infinite-scroll-array';
import { ChevronAnimatedIcon, CrossFilledIconGlyph, MoveToEndIconGlyph, MoveToStartIconGlyph, SearchIconGlyph } from '@shapeable/icons';
import { DndContext, closestCenter, DragOverlay, useSensors, useSensor, PointerSensor } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { OptionMenuInputSelection } from './option-menu-input-selection';
import { ClipLoader } from 'react-spinners';
import { useThemeEval } from '../hooks/use-theme-eval';
import { OptionMenuLoadingMessage } from './option-menu-loading-message';
import { MenuPortal } from '../elements/menu-portal';
import { useDndDialogModifier } from './hooks/use-dnd-dialog-modifier';
var cls = classNames('popup-menu-input');
var MODIFIER_KEYS = ["Shift", "Control", "Alt", "Meta", "CapsLock"];
// This component binds a text input (built-in or custom) 
// with a popup-menu to provide keyboard control for the menu
export var selectionMenu = {
  id: 'selection-menu',
  items: [{
    id: 'remove',
    label: 'Remove',
    iconComponent: CrossFilledIconGlyph
  }, {
    id: 'start',
    label: 'Move To Start',
    iconComponent: MoveToStartIconGlyph
  }, {
    id: 'end',
    label: 'Move To End',
    iconComponent: MoveToEndIconGlyph
  }]
};
export var OptionMenuInputDefaultProps = {
  applyFilter: true,
  minFilterLength: 1,
  usePortal: false,
  useMenuTransitions: true,
  font: 'sans',
  showIcon: true,
  showMenuIndicator: false,
  showClearButton: true,
  inputValueDebounce: 700,
  iconProps: {
    size: 16,
    filterComponent: SearchIconGlyph,
    filterSize: 16
  },
  inputProps: {
    activeCursorType: 'text',
    clearOnSelect: true
  },
  clearButtonProps: {
    component: CrossFilledIconGlyph,
    size: 18,
    colors: {
      fill: 'mid',
      hover: {
        fill: 'strong'
      }
    }
  },
  menuIndicatorProps: {
    component: ChevronAnimatedIcon,
    activeComponent: ChevronAnimatedIcon,
    size: 14,
    colors: {
      fill: 'mid',
      hover: {
        fill: 'strong'
      }
    }
  },
  menuIndicatorDividerProps: {
    colors: {
      border: 'just-visible'
    }
  },
  selectionsProps: {},
  selectionMenuProps: {
    style: {
      minWidth: '150px'
    },
    itemProps: {
      labelProps: {
        style: {
          whiteSpace: 'nowrap'
        }
      }
    }
  },
  activePlaceholder: 'Filter...',
  bodyProps: {
    borderRadius: 8,
    borderWidth: 1,
    verticalPadding: 8,
    horizontalPadding: 14,
    showFocus: true,
    startProps: {
      gap: 8
    },
    endProps: {
      gap: 12
    },
    colors: {
      active: {
        border: 'focus-highlight'
      },
      focusWithin: {
        border: 'focus-highlight'
      },
      border: 'just-visible',
      hover: {
        border: 'subtle'
      }
    }
  },
  colors: {
    background: 'transparent',
    text: 'strong'
  },
  hideOnClickOutside: true,
  menuProps: {
    loadingMoreText: 'Please wait...',
    headProps: {
      horizontalPadding: 12,
      verticalPadding: 8,
      comfortPadding: 4
    },
    footProps: {
      horizontalPadding: 12,
      verticalPadding: 8,
      comfortPadding: 4
    }
  },
  menuTransitionValues: {
    from: {
      opacity: 0,
      transform: "scale(0.96) translateY(-4px)"
    },
    enter: {
      opacity: 1,
      transform: "scale(1) translateY(0px)"
    }
  },
  selectionTransitionValues: {
    from: {
      opacity: 0,
      transform: "scale(0.96) translateY(-4px)"
    },
    enter: {
      opacity: 1,
      transform: "scale(1) translateY(0px)"
    },
    leave: {
      opacity: 0,
      transform: "scale(1) translateY(0px)"
    }
  },
  defaultValue: '',
  highlightTextClassName: 'shp--text-highlight'
};
// -------- Styles -------->
var ContainerStyles = breakpoints({
  base: css(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n    ", ";\n    color: ", ";\n\n    ", "\n\n    font-size: ", ";\n    display: flex;\n    position: relative;\n    flex-direction: column;\n\n    ", "\n  "], ["\n    ", ";\n    color: ", ";\n\n    ", "\n\n    font-size: ", ";\n    display: flex;\n    position: relative;\n    flex-direction: column;\n\n    ", "\n  "])), theme.APPLY_COLORS, theme.COLOR('text'), function (_a) {
    var font = _a.font;
    return css(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n      font-family: ", ";\n    "], ["\n      font-family: ", ";\n    "])), theme.FONT(font));
  }, theme.FONT_SIZE(16), function (_a) {
    var highlightTextClassName = _a.highlightTextClassName;
    return highlightTextClassName && css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n      .", " {\n        color: ", ";\n        background-color: transparent;\n      }\n    "], ["\n      .", " {\n        color: ", ";\n        background-color: transparent;\n      }\n    "])), highlightTextClassName, theme.COLOR('text-highlight'));
  }),
  desktop: css(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n    font-size: ", ";\n  "], ["\n    font-size: ", ";\n  "])), theme.FONT_SIZE(15))
});
var BodyStyles = breakpoints({
  base: css(templateObject_8 || (templateObject_8 = __makeTemplateObject(["\n    ", ";\n    display: flex;\n    flex-direction: column;\n  \n    ", "\n\n    ", "\n  "], ["\n    ", ";\n    display: flex;\n    flex-direction: column;\n  \n    ", "\n\n    ", "\n  "])), theme.APPLY_COLORS, function (_a) {
    var showFocus = _a.showFocus,
      borderWidth = _a.borderWidth;
    return showFocus && css(templateObject_6 || (templateObject_6 = __makeTemplateObject(["\n      border-style: solid;\n      border-width: ", "px;\n      &:focus-within {\n        box-shadow: 0 0 3px 1px ", ";\n      }\n      \n      ", "\n    "], ["\n      border-style: solid;\n      border-width: ", "px;\n      &:focus-within {\n        box-shadow: 0 0 3px 1px ", ";\n      }\n      \n      ", "\n    "])), borderWidth, theme.COLOR('focus-shadow'), function (_a) {
      var isActive = _a.isActive;
      return isActive && css(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n        box-shadow: 0 0 3px 1px ", ";\n      "], ["\n        box-shadow: 0 0 3px 1px ", ";\n      "])), theme.COLOR('focus-shadow'));
    });
  }, function (_a) {
    var borderWidth = _a.borderWidth,
      borderRadius = _a.borderRadius,
      cursorType = _a.cursorType,
      _b = _a.horizontalPadding,
      horizontalPadding = _b === void 0 ? 12 : _b,
      _c = _a.verticalPadding,
      verticalPadding = _c === void 0 ? 8 : _c;
    return css(templateObject_7 || (templateObject_7 = __makeTemplateObject(["\n      cursor: ", ";\n      border-radius: ", "px;\n      padding: ", "px ", "px;\n    "], ["\n      cursor: ", ";\n      border-radius: ", "px;\n      padding: ", "px ", "px;\n    "])), cursorType, borderRadius, verticalPadding, horizontalPadding);
  })
});
var ControlStyles = breakpoints({
  base: css(templateObject_9 || (templateObject_9 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n  "], ["\n    display: flex;\n    flex-direction: row;\n    align-items: center;\n  "])))
});
var StartStyles = breakpoints({
  base: css(templateObject_11 || (templateObject_11 = __makeTemplateObject(["\n    align-items: center;\n    display: flex;\n    flex-grow: 1;\n    flex-direction: row;\n    justify-self: stretch;\n    ", "\n  "], ["\n    align-items: center;\n    display: flex;\n    flex-grow: 1;\n    flex-direction: row;\n    justify-self: stretch;\n    ", "\n  "])), function (_a) {
    var gap = _a.gap;
    return css(templateObject_10 || (templateObject_10 = __makeTemplateObject(["\n      gap: ", "px;\n    "], ["\n      gap: ", "px;\n    "])), gap);
  })
});
var EndStyles = breakpoints({
  base: css(templateObject_13 || (templateObject_13 = __makeTemplateObject(["\n    align-items: center;\n    display: flex;\n    align-self: stretch;\n    flex-direction: row;\n    ", "\n  "], ["\n    align-items: center;\n    display: flex;\n    align-self: stretch;\n    flex-direction: row;\n    ", "\n  "])), function (_a) {
    var gap = _a.gap;
    return css(templateObject_12 || (templateObject_12 = __makeTemplateObject(["\n      padding-left: ", "px;\n      gap: ", "px;\n    "], ["\n      padding-left: ", "px;\n      gap: ", "px;\n    "])), gap, gap);
  })
});
// we'll provide some default styling for the input component, but 
var InputStyles = breakpoints({
  base: css(templateObject_15 || (templateObject_15 = __makeTemplateObject(["\n    font-family: inherit; \n    font-size: 1em;\n    flex-grow: 1;\n    border: none;\n    min-height: 20px;\n    box-sizing: border-box;\n    justify-self: stretch;\n    outline: none;\n    ", "\n  "], ["\n    font-family: inherit; \n    font-size: 1em;\n    flex-grow: 1;\n    border: none;\n    min-height: 20px;\n    box-sizing: border-box;\n    justify-self: stretch;\n    outline: none;\n    ", "\n  "])), function (_a) {
    var cursorType = _a.cursorType;
    return css(templateObject_14 || (templateObject_14 = __makeTemplateObject(["\n      cursor: ", ";\n    "], ["\n      cursor: ", ";\n    "])), cursorType);
  })
});
var LabelStyles = breakpoints({
  base: css(templateObject_16 || (templateObject_16 = __makeTemplateObject(["\n    font-size: 0.8em;\n    color: ", ";\n    margin-bottom: 4px;\n    font-weight: 400;\n  "], ["\n    font-size: 0.8em;\n    color: ", ";\n    margin-bottom: 4px;\n    font-weight: 400;\n  "])), theme.COLOR('text-subtle'))
});
var MenuPositionStyles = breakpoints({
  base: css(templateObject_17 || (templateObject_17 = __makeTemplateObject(["\n    /* position: relative; */\n    tab-index: -1;\n    height: 0;\n    width: 100%;\n    overflow: visible;\n  "], ["\n    /* position: relative; */\n    tab-index: -1;\n    height: 0;\n    width: 100%;\n    overflow: visible;\n  "])))
});
var MenuStyles = breakpoints({
  base: css(templateObject_19 || (templateObject_19 = __makeTemplateObject(["\n    tab-index: -1;\n    color: inherit;\n    box-sizing: border-box;\n    box-shadow: 0px 4px 6px rgba(0,0,0,0.1);\n    width: 100%;\n    top: 0;\n    left: 0;\n    z-index: 100;\n\n    ", "\n\n  "], ["\n    tab-index: -1;\n    color: inherit;\n    box-sizing: border-box;\n    box-shadow: 0px 4px 6px rgba(0,0,0,0.1);\n    width: 100%;\n    top: 0;\n    left: 0;\n    z-index: 100;\n\n    ", "\n\n  "])), function (_a) {
    var font = _a.font;
    return css(templateObject_18 || (templateObject_18 = __makeTemplateObject(["\n      font-family: ", ";\n    "], ["\n      font-family: ", ";\n    "])), theme.FONT(font));
  })
});
var SelectionsStyles = breakpoints({
  base: css(templateObject_20 || (templateObject_20 = __makeTemplateObject(["\n    min-height: 20px;\n    display: flex;\n    flex-direction: row;\n    flex-wrap: wrap;\n    gap: 12px 20px;\n    pointer-events: auto;\n  "], ["\n    min-height: 20px;\n    display: flex;\n    flex-direction: row;\n    flex-wrap: wrap;\n    gap: 12px 20px;\n    pointer-events: auto;\n  "])))
});
var InputContainerStyles = breakpoints({
  base: css(templateObject_22 || (templateObject_22 = __makeTemplateObject(["\n    display: flex;\n    align-items: center;\n    flex-direction: row;\n    align-items: center;\n    ", "\n  "], ["\n    display: flex;\n    align-items: center;\n    flex-direction: row;\n    align-items: center;\n    ", "\n  "])), function (_a) {
    var gap = _a.gap;
    return css(templateObject_21 || (templateObject_21 = __makeTemplateObject(["\n      gap: ", "px;\n    "], ["\n      gap: ", "px;\n    "])), gap);
  })
});
var HiddenInputStyles = breakpoints({
  base: css(templateObject_23 || (templateObject_23 = __makeTemplateObject(["\n    height: 0;\n    width: 0;\n    font-size: 15px;\n    position: absolute;\n    top: -900px;\n    left: -900px;\n  "], ["\n    height: 0;\n    width: 0;\n    font-size: 15px;\n    position: absolute;\n    top: -900px;\n    left: -900px;\n  "])))
});
var MenuIndicatorDividerStyles = breakpoints({
  base: css(templateObject_24 || (templateObject_24 = __makeTemplateObject(["\n    ", ";\n    border-style: solid;\n    border-width: 0 0.75px;\n    width: 0;\n    height: 100%;\n  "], ["\n    ", ";\n    border-style: solid;\n    border-width: 0 0.75px;\n    width: 0;\n    height: 100%;\n  "])), theme.APPLY_COLORS)
});
var SelectionStyles = breakpoints({
  base: css(templateObject_25 || (templateObject_25 = __makeTemplateObject(["\n    \n  "], ["\n    \n  "])))
});
var LoaderStyles = breakpoints({
  base: css(templateObject_26 || (templateObject_26 = __makeTemplateObject(["\n    border-color: ", " !important;\n  "], ["\n    border-color: ", " !important;\n  "])), theme.COLOR('subtle'))
});
var LoadingMessageStyles = breakpoints({
  base: css(templateObject_27 || (templateObject_27 = __makeTemplateObject(["\n    \n  "], ["\n    \n  "])))
});
var MenuHeadStyles = breakpoints({
  base: css(templateObject_28 || (templateObject_28 = __makeTemplateObject(["\n    display: flex;\n    flex-direction: column;\n    gap: 8px;\n  "], ["\n    display: flex;\n    flex-direction: column;\n    gap: 8px;\n  "])))
});
// -------- Components -------->
var My = {
  NAME: 'OptionMenuInput',
  Container: styled.div(templateObject_29 || (templateObject_29 = __makeTemplateObject(["", ""], ["", ""])), ContainerStyles),
  Body: styled.div(templateObject_30 || (templateObject_30 = __makeTemplateObject(["", ""], ["", ""])), BodyStyles),
  Loader: styled(ClipLoader)(templateObject_31 || (templateObject_31 = __makeTemplateObject(["", ""], ["", ""])), LoaderStyles),
  Label: styled.label(templateObject_32 || (templateObject_32 = __makeTemplateObject(["", ""], ["", ""])), LabelStyles),
  Control: styled.div(templateObject_33 || (templateObject_33 = __makeTemplateObject(["", ""], ["", ""])), ControlStyles),
  Start: styled.div(templateObject_34 || (templateObject_34 = __makeTemplateObject(["", ""], ["", ""])), StartStyles),
  Selections: styled.div(templateObject_35 || (templateObject_35 = __makeTemplateObject(["", ""], ["", ""])), SelectionsStyles),
  Selection: styled(OptionMenuInputSelection)(templateObject_36 || (templateObject_36 = __makeTemplateObject(["", ""], ["", ""])), SelectionStyles),
  End: styled.div(templateObject_37 || (templateObject_37 = __makeTemplateObject(["", ""], ["", ""])), EndStyles),
  MenuIndicatorDivider: styled.div(templateObject_38 || (templateObject_38 = __makeTemplateObject(["", ""], ["", ""])), MenuIndicatorDividerStyles),
  InputContainer: styled.div(templateObject_39 || (templateObject_39 = __makeTemplateObject(["", ""], ["", ""])), InputContainerStyles),
  Input: styled.input(templateObject_40 || (templateObject_40 = __makeTemplateObject(["", ""], ["", ""])), InputStyles),
  HiddenInput: styled.input(templateObject_41 || (templateObject_41 = __makeTemplateObject(["", ""], ["", ""])), HiddenInputStyles),
  LoadingMessage: styled(OptionMenuLoadingMessage)(templateObject_42 || (templateObject_42 = __makeTemplateObject(["", ""], ["", ""])), LoadingMessageStyles),
  MenuPosition: styled.div(templateObject_43 || (templateObject_43 = __makeTemplateObject(["", ""], ["", ""])), MenuPositionStyles),
  Menu: styled(OptionMenu)(templateObject_44 || (templateObject_44 = __makeTemplateObject(["", ""], ["", ""])), MenuStyles),
  MenuHead: styled.div(templateObject_45 || (templateObject_45 = __makeTemplateObject(["", ""], ["", ""])), MenuHeadStyles)
};
export var OptionMenuInput = function (props) {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  var componentProps = useComponentProps(My.NAME, OptionMenuInputDefaultProps, props);
  var defaultState = {
    inputValue: '',
    timestamp: Date.now(),
    cursor: 0,
    visibleMenuIds: []
  };
  var id = componentProps.id,
    label = componentProps.label,
    isLoading = componentProps.isLoading,
    inputValueDebounce = componentProps.inputValueDebounce,
    className = componentProps.className,
    children = componentProps.children,
    defaultValue = componentProps.defaultValue,
    menu = componentProps.menu,
    onChange = componentProps.onChange,
    onSelect = componentProps.onSelect,
    applyFilter = componentProps.applyFilter,
    minFilterLength = componentProps.minFilterLength,
    activePlaceholder = componentProps.activePlaceholder,
    highlightTextClassName = componentProps.highlightTextClassName,
    menuProps = componentProps.menuProps,
    menuTransitionValues = componentProps.menuTransitionValues,
    showIcon = componentProps.showIcon,
    iconProps = componentProps.iconProps,
    font = componentProps.font,
    menuIndicatorProps = componentProps.menuIndicatorProps,
    menuIndicatorDividerProps = componentProps.menuIndicatorDividerProps,
    showClearButton = componentProps.showClearButton,
    clearButtonProps = componentProps.clearButtonProps,
    inputProps = componentProps.inputProps,
    usePortal = componentProps.usePortal,
    useMenuTransitions = componentProps.useMenuTransitions,
    selectionMenuProps = componentProps.selectionMenuProps,
    selectionsProps = componentProps.selectionsProps,
    selectionTransitionValues = componentProps.selectionTransitionValues,
    bodyProps = componentProps.bodyProps,
    colors = componentProps.colors;
  var $ = useThemeEval();
  // assume that if we pass in "selectedItems" (plural) rather than "selectedItem", we allow for multiple selections
  var allowMultipleSelections = componentProps.allowMultipleSelections === undefined ? !!componentProps.selectedItems : componentProps.allowMultipleSelections;
  var allowSorting = componentProps.allowSorting === undefined ? !!allowMultipleSelections : componentProps.allowSorting;
  var showMenuIndicator = componentProps.showMenuIndicator === undefined ? !!onSelect : componentProps.showMenuIndicator;
  var isLoadingInitialItems = isLoading && !((_a = menu.items) === null || _a === void 0 ? void 0 : _a.length);
  // setup a flag to indicate if this is a controlled input
  // assume if we want the menu indicator, we also want the divider, unless explicitly unset
  var showMenuIndicatorDivider = componentProps.showMenuIndicatorDivider === undefined ? !!showMenuIndicator : componentProps.showMenuIndicatorDivider;
  var hideOnBlur = componentProps.hideOnBlur === undefined ? true : componentProps.hideOnBlur;
  var showOnInputClick = componentProps.showOnInputClick === undefined ? !!showMenuIndicator : componentProps.showOnInputClick;
  var _j = __read(useState(null), 2),
    activeDragId = _j[0],
    setActiveDragId = _j[1];
  var _k = __read(useState(null), 2),
    activeDragItem = _k[0],
    setActiveDragItem = _k[1];
  var _l = __read(React.useState(defaultState), 2),
    state = _l[0],
    setState = _l[1];
  var inputValue = state.inputValue;
  var patchState = function (args) {
    var patch = args.patch,
      eventName = args.eventName,
      event = args.event;
    if (size(patch) === 0) return;
    ginni.verbose.log("PATCHING STATE %c".concat(eventName, "%c"), "color: #98C379;", '', patch);
    setState(function (prevState) {
      // here, we can possibly store the previous selections and values, and add undo support 
      return __assign(__assign({}, prevState), patch);
    });
  };
  // console.log("MENU INPUT menu", menu);
  // we want the control to appear focussed if any focus refs are active (control, hidden, and menu head)
  var bodyIsActive = !!((_b = state.focusRef) === null || _b === void 0 ? void 0 : _b.current);
  // const [ value, setValue ] = React.useState(defaultValue);
  // const [ timestamp, setTimestamp ] = React.useState(Date.now());
  // const [ cursor, setCursor ] = React.useState(0);
  // const [ visibleMenuIds, setVisibleMenuIds ] = React.useState<OptionMenuId[]>([]);
  // console.log("bodyIsActive", bodyIsActive, state.focusRef);
  var selections = componentProps.selectedItem && [componentProps.selectedItem] || componentProps.selectedItems || [];
  ginni.verbose.log("Option Menu Input Selections", selections);
  // if we show a menu indicator, make the cursor a pointer which feels more like a "select" action. otherwise, it feels more like a text box or search box
  var filteredItems = applyFilter ? menu.items.filter(function (item) {
    return item.label.toLowerCase().includes(inputValue.toLowerCase());
  }) : menu.items;
  var defaultInfiniteScroll = useInfiniteScrollArray(__assign({
    all: filteredItems
  }, (menuProps === null || menuProps === void 0 ? void 0 : menuProps.infiniteScrollConfig) || {}));
  // get the true infinite scroll. This allows us to implement a different one externally, and feed it in.
  var infiniteScroll = ((_c = componentProps.menuProps) === null || _c === void 0 ? void 0 : _c.infiniteScroll) || defaultInfiniteScroll;
  var sentryRef = infiniteScroll.sentryRef,
    rootRef = infiniteScroll.rootRef,
    hasNextPage = infiniteScroll.hasNextPage,
    loading = infiniteScroll.loading;
  // don't pull the items from infinite scroll if it's not the default setup!
  var finalItems = !!defaultInfiniteScroll ? defaultInfiniteScroll.items : filteredItems;
  var hiddenInputRef = useRef(null); // ref for the hidden input, used when there is a single selection in the control
  var inputRef = useRef(null); // ref for the input in the control
  var inputContainerRef = useRef(null); // ref for the input container in the control
  var menuHeadContainerRef = useRef(null); // ref for the container for the input in the menu head (which also contains the icon)
  var menuFootRef = useRef(null);
  var selectionsRef = useRef(null); // ref for the selections container
  // set the items 
  var menuToRender = applyFilter && inputValue.length >= minFilterLength ? __assign(__assign({}, menu), {
    items: finalItems
  }) : menu;
  var headProps = menuProps === null || menuProps === void 0 ? void 0 : menuProps.headProps;
  var footProps = menuProps === null || menuProps === void 0 ? void 0 : menuProps.footProps;
  var menus = flattenMenu(menuToRender);
  var menusById = keyBy(menus, 'id');
  var toggleOnClick = showMenuIndicator;
  var visibleMenus = state.visibleMenuIds.map(function (id) {
    return menusById[id];
  });
  // setup the menus and menu item which have active focus
  var activeMenu = visibleMenus.length ? visibleMenus[visibleMenus.length - 1] : null;
  var activeMenuItems = activeMenu === null || activeMenu === void 0 ? void 0 : activeMenu.items;
  var activeMenuItem = activeMenuItems ? activeMenuItems[state.cursor] : null;
  var isActive = !!activeMenu;
  var bodyCursorType = (bodyProps === null || bodyProps === void 0 ? void 0 : bodyProps.cursorType) === undefined ? showMenuIndicator ? 'pointer' : 'text' : bodyProps === null || bodyProps === void 0 ? void 0 : bodyProps.cursorType;
  var inputCursorType = (isActive ? (inputProps === null || inputProps === void 0 ? void 0 : inputProps.activeCursorType) || (inputProps === null || inputProps === void 0 ? void 0 : inputProps.cursorType) : inputProps === null || inputProps === void 0 ? void 0 : inputProps.cursorType) || bodyCursorType;
  var PlaceholderComponent = componentProps.placeholderComponent;
  var SelectedItemComponent = componentProps.selectedItemComponent || ((_d = componentProps.menuProps) === null || _d === void 0 ? void 0 : _d.itemComponent);
  var hasSelection = selections.length > 0 && SelectedItemComponent;
  var Icon = iconProps === null || iconProps === void 0 ? void 0 : iconProps.component;
  var icon = showIcon && Icon && React.createElement(Icon, {
    colors: {
      fill: 'strong'
    },
    size: iconProps.size
  });
  var FilterIcon = (iconProps === null || iconProps === void 0 ? void 0 : iconProps.filterComponent) || (iconProps === null || iconProps === void 0 ? void 0 : iconProps.component);
  var filterIcon = showIcon && FilterIcon && React.createElement(FilterIcon, {
    colors: {
      fill: 'strong'
    },
    size: iconProps.filterSize || iconProps.size
  });
  var placeholder = componentProps.placeholder;
  var showPlaceholder = componentProps.showPlaceholder === undefined ? !!placeholder : componentProps.showPlaceholder;
  // placeholder must be visible if there is no icon
  if (!Icon && !showPlaceholder) {
    showPlaceholder = true;
    placeholder = 'Select an item...';
  }
  var MenuIndicatorIcon = isActive ? (menuIndicatorProps === null || menuIndicatorProps === void 0 ? void 0 : menuIndicatorProps.activeComponent) || (menuIndicatorProps === null || menuIndicatorProps === void 0 ? void 0 : menuIndicatorProps.component) : menuIndicatorProps === null || menuIndicatorProps === void 0 ? void 0 : menuIndicatorProps.component;
  var ClearIcon = showClearButton && hasSelection && (clearButtonProps === null || clearButtonProps === void 0 ? void 0 : clearButtonProps.component);
  var MenuIndicatorDivider = (menuIndicatorDividerProps === null || menuIndicatorDividerProps === void 0 ? void 0 : menuIndicatorDividerProps.component) || My.MenuIndicatorDivider;
  var showEnd = showMenuIndicator && MenuIndicatorIcon;
  var hasRegularInput = !hasSelection && !PlaceholderComponent && showPlaceholder;
  ginni.verbose.log("CURRENT STATE", state);
  // setup an effect to for when the inout changes, which can be throttled to prevent too many updates
  var debouncedOnChange = useMemo(function () {
    return debounce(function (value) {
      onChange({
        value: value,
        event: null
      });
    }, inputValueDebounce);
  }, [onChange]);
  useEffect(function () {
    debouncedOnChange(inputValue);
  }, [inputValue, debouncedOnChange]);
  var transitions = useTransition(state.visibleMenuIds, __assign(__assign({}, menuTransitionValues), {
    config: {
      tension: 250,
      friction: 20
    }
  }));
  var selectionTransitionItems = useTransition(selections, __assign(__assign({}, selectionTransitionValues), {
    config: function (item, index, phase) {
      if (phase === "enter") return {
        duration: 300
      }; // Slower enter
      if (phase === "leave") return {
        duration: 100
      }; // Faster exit
      return {
        tension: 250,
        friction: 20
      };
    }
  }));
  var sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10
      // delay: 0, // ms delay before drag starts
      // tolerance: 15, // movement threshold in px before drag can start
    }
  }));
  // setup a drag effect to map the active drag id to an actual selection item
  useEffect(function () {
    if (activeDragId) {
      var item = selections.find(function (item) {
        return item.id === activeDragId;
      });
      setActiveDragItem(item);
    } else {
      setActiveDragItem(null);
    }
  }, [activeDragId, selections]);
  useEffect(function () {
    var _a;
    ginni.verbose.log("Option Menu Input ".concat(id, ": Focus Ref"), (_a = state.focusRef) === null || _a === void 0 ? void 0 : _a.current);
    setTimeout(function () {
      var _a;
      if ((_a = state.focusRef) === null || _a === void 0 ? void 0 : _a.current) {
        state.focusRef.current.focus({
          preventScroll: true
        });
      }
    }, 100);
  }, [(_e = state.focusRef) === null || _e === void 0 ? void 0 : _e.current]);
  var handleBodyClick = useCallback(function (event) {
    event.stopPropagation();
    event.preventDefault();
    ginni.verbose.log("handle body click", event.target, inputRef.current, state.focusRef);
    var patch = {};
    if (isActive && toggleOnClick) {
      patch.visibleMenuIds = [];
      patch.cursor = 0;
      if (!hasRegularInput) {
        ginni.verbose.log("focusing hidden input");
        patch.focusRef = hiddenInputRef;
      } else {
        ginni.verbose.log("focusing input");
        patch.focusRef = inputRef;
      }
    } else {
      // console.log("body on click inputRef", activeInputContainerRef);
      ginni.verbose.log("focusing input ref");
      patch = {
        cursor: 0,
        visibleMenuIds: [menu.id],
        focusRef: inputRef
      };
    }
    patchState({
      patch: patch,
      eventName: 'handleBodyClick'
    });
  }, [isActive, inputRef === null || inputRef === void 0 ? void 0 : inputRef.current]);
  var handleBodyMouseDown = useCallback(function (event) {
    // prevent the input blur from firing before the menu click
    var _a;
    if (isActive) {
      if (!((_a = selectionsRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) && (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) !== event.target) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }, [isActive]);
  var handleSelectionsClick = useCallback(function (event) {
    var _a;
    event.stopPropagation();
    var patch = {};
    if (!hasRegularInput) {
      patch.focusRef = hiddenInputRef;
    } else {
      patch.focusRef = inputRef;
    }
    // show the menu when clicking the selection (if there is one)
    if (!isActive && (((_a = selectionsRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) || (document === null || document === void 0 ? void 0 : document.activeElement) !== (inputRef === null || inputRef === void 0 ? void 0 : inputRef.current))) {
      patch.visibleMenuIds = [menu.id];
      patchState({
        patch: patch,
        eventName: 'handleSelectionsClick'
      });
    }
  }, [isActive, hasSelection]);
  // common keystroke handling for the normal and hidden inputs
  var processKeyEvent = useCallback(function (e) {
    var _a;
    var patch = {};
    // console.log(e.key);
    if (MODIFIER_KEYS.includes(e.key)) {
      return null;
    }
    if (e.key === 'Tab' || e.key === 'Escape') {
      patch.visibleMenuIds = [];
      patch.cursor = 0;
      patch.inputValue = '';
    }
    if (e.key === 'Tab') {
      // if the user presses tab, we'll hide the menu
      patch.focusRef = null;
      (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.blur();
      return patch;
    }
    if (e.key === 'Escape') {
      if (!hasRegularInput) {
        patch.focusRef = hiddenInputRef;
      } else {
        patch.focusRef = inputRef;
      }
      return patch;
    }
    if (e.key === 'Enter') {
      if (isActive) {
        if (activeMenuItem) {
          handleSelect({
            item: activeMenuItem,
            event: e
          });
          return; // let handleSelect do the patch
        }
      }
    }
    if (e.key === '\\') {
      // Prevent the input of the backslash key
      e.preventDefault();
    }
    if (includes(['ArrowUp', 'ArrowDown'], e.key)) {
      e.preventDefault();
    }
    if (!activeMenu) {
      // open the menu when certain keys are pressed
      if (!includes(['Tab'], e.key)) {
        // if the user presses the arrow keys, we'll show the menu
        patch.visibleMenuIds = [menu.id];
      }
    } else {
      if (e.key === 'ArrowUp') {
        // add menu scrolling with arrow keys
        e.preventDefault();
        patch.cursor = Math.max(0, state.cursor - 1);
      }
      if (e.key === 'ArrowDown') {
        // add menu scrolling with arrow keys
        e.preventDefault();
        patch.cursor = Math.min(activeMenuItems.length - 1, state.cursor + 1);
      }
      if (e.key === 'ArrowRight') {
        if (activeMenuItem === null || activeMenuItem === void 0 ? void 0 : activeMenuItem.menu) {
          // add menu scrolling with arrow keys
          e.preventDefault();
          // this would check if the current item has any children and then push that child as a visibleMenu
        }
      }
    }
    // console.log("KEYDOWN PATCH", patch);
    return patch;
  }, [activeMenu, hasSelection, activeMenuItems === null || activeMenuItems === void 0 ? void 0 : activeMenuItems.length, activeMenuItem, state.cursor, state.visibleMenuIds]);
  // --- standard input events ----- */
  var handleInputClick = useCallback(function (event) {
    event.stopPropagation();
    var patch = {};
    if (!isActive) {
      // we'll need to show the menu
      patch.visibleMenuIds = [menu.id];
      patch.focusRef = inputRef;
    }
    if (isActive) {}
    patchState({
      patch: patch,
      eventName: 'handleInputClick'
    });
    // if (!isActive || (inputRef.current && document?.activeElement !== activeInputRef?.current)) {
    //   showMenu();
    // }
  }, [isActive]);
  var handleInputChange = function (event) {
    var value = event.target.value;
    var patch = {
      inputValue: value,
      cursor: 0
    };
    if (applyFilter && value.length >= minFilterLength) {
      patch.visibleMenuIds = [menu.id];
    }
    // if (onChange) {
    //   onChange({ value, event });
    // }
    ginni.verbose.log("INPUT CHANGE VALUE", value);
    ginni.verbose.log("INPUT CHANGE PATCH", patch);
    patchState({
      patch: patch,
      event: event,
      eventName: 'handleInputChange'
    });
  };
  var handleInputFocus = function () {
    var patch = {
      focusRef: inputRef,
      timestamp: Date.now()
    };
    patchState({
      patch: patch,
      eventName: 'handleInputFocus'
    });
  };
  // this might need to support x-y navigation too, in the case of a grid-based dropdown (like a card grid)
  var handleInputKeyDown = useCallback(function (e) {
    ginni.verbose.log("Input Key Down ".concat(id, " Key Down"), e.key);
    var patch = processKeyEvent(e);
    if (!patch) return;
    patchState({
      patch: patch,
      eventName: 'handleInputKeyDown'
    });
  }, [activeMenu, activeMenuItem, activeMenuItems === null || activeMenuItems === void 0 ? void 0 : activeMenuItems.length, state.cursor, state.visibleMenuIds]);
  // --- hidden input events ----- */
  var handleHiddenInputFocus = function (event) {
    // console.log('handleHiddenInputFocus');
    var patch = {
      focusRef: hiddenInputRef
    };
    patchState({
      patch: patch,
      eventName: 'handleHiddenInputFocus'
    });
  };
  var handleHiddenInputChange = useCallback(function (event) {
    var value = event.target.value;
    ginni.verbose.log("HIDDEN INPUT VALUE", value);
    var patch = {
      inputValue: value,
      cursor: 0
    };
    if (applyFilter && value.length >= minFilterLength) {
      patch.visibleMenuIds = [menu.id];
    }
    // if (onChange) {
    //   onChange({ value, event });
    // }
    ginni.verbose.log("HIDDEN INPUT CHANGE VALUE", value);
    ginni.verbose.log("HIDDEN INPUT CHANGE PATCH", patch);
    patchState({
      patch: patch,
      eventName: 'handleHiddenInputChange'
    });
  }, [inputRef]);
  var handleHiddenInputKeyDown = useCallback(function (e) {
    // we don't do this for tabs as we need tabs to work as expected
    // console.log(e.key);
    ginni.verbose.log("Option Menu Input ".concat(id, " - Hidden Input Key Down"), e.key, isActive);
    // if (e.key !== 'Tab') {
    //   e.stopPropagation();
    //   e.preventDefault();
    //   showMenu();
    // }
    var patch = processKeyEvent(e) || {};
    if (e.key === 'Backspace') {
      handleSelect({
        item: null,
        event: e
      });
      return; // let handleSelect do the patch
    } else {
      if (!["Shift", "Control", "Alt", "Meta", "CapsLock"].includes(e.key)) {
        // this needs to be more specific
        if (!hasRegularInput) {
          patch.focusRef = inputRef;
        }
      }
    }
    patchState({
      patch: patch,
      eventName: 'handleHiddenInputKeyDown'
    });
  }, [activeMenu, activeMenuItem, hasSelection, inputRef === null || inputRef === void 0 ? void 0 : inputRef.current, activeMenuItems === null || activeMenuItems === void 0 ? void 0 : activeMenuItems.length, state.cursor, state.visibleMenuIds]); //[inputRef?.current, isActive]);
  var handleHiddenInputBlur = function (event) {
    patchState({
      patch: {
        focusRef: null
      },
      eventName: 'handleHiddenInputBlur'
    });
  };
  // --- selection handling ----- */
  var handleSelect = function (args) {
    ginni.verbose.log("Option Menu Input ".concat(id, " - Handle Select"), args);
    var patch = {};
    if (inputProps.clearOnSelect) {
      patch.inputValue = '';
    }
    var items = !args.item ? [] : allowMultipleSelections ? selections.some(function (selection) {
      return selection.id === args.item.id;
    }) ? selections.filter(function (selection) {
      return selection.id !== args.item.id;
    }) : selections.concat(args.item) : [args.item];
    var onSelectArgs = __assign(__assign({}, args), {
      items: items
    });
    if (onSelect) {
      var result = onSelect(onSelectArgs);
      // allow the select handler to prevent the menu from closing
      if (result !== false) {
        patch.visibleMenuIds = [];
        patch.focusRef = hiddenInputRef;
        patch.cursor = 0;
        if (!args.item) {
          // this is a clear, so we'll move focus back to the standard (non-hidden) input
          patch.focusRef = inputRef;
        }
        patchState({
          patch: patch,
          eventName: 'handleSelect'
        });
      }
    }
  };
  var handleClearClick = function (event) {
    event.stopPropagation();
    event.preventDefault();
    handleSelect({
      item: null,
      event: event
    });
  };
  var Input = inputProps.component || My.Input;
  var triggerRef = useRef(null);
  var bodyRef = useRef(null);
  var menuPortalRef = useRef(null);
  var _m = useOptionMenuPosition({
      triggerRef: triggerRef,
      isOpen: !!activeMenu,
      headRef: menuHeadContainerRef,
      footRef: menuFootRef,
      headPadding: ((headProps === null || headProps === void 0 ? void 0 : headProps.verticalPadding) || 0) * 2 + ((headProps === null || headProps === void 0 ? void 0 : headProps.comfortPadding) || 0),
      footPadding: ((footProps === null || footProps === void 0 ? void 0 : footProps.verticalPadding) || 0) * 2 + ((footProps === null || footProps === void 0 ? void 0 : footProps.comfortPadding) || 0),
      deps: [finalItems.length, activeMenu, menuHeadContainerRef, state.timestamp, selections.length, loading, hasNextPage]
    }),
    position = _m.position,
    maxHeight = _m.maxHeight,
    maxItemsHeight = _m.maxItemsHeight,
    menuItemsRef = _m.menuItemsRef;
  var positionStyle = {
    top: position === "below" ? "100%" : undefined,
    bottom: position === "above" ? "100%" : undefined,
    maxHeight: maxHeight,
    overflowY: maxHeight ? "auto" : "hidden"
  };
  // console.log(triggerRef, 'position', position, 'maxHeight', maxHeight);
  // const handleItemMouseOver: OptionMenuOnItemMouseOverHandler = ({ event, index, item }) => {
  //   // we'll disable this for the moment, as it causes issues with constant scrolling
  //   setCursor(index);
  // };
  useEffect(function () {
    var menu = menuItemsRef.current;
    if (!menu) return;
    var preventScrollPropagation = function (event) {
      if (!(event.target instanceof HTMLElement)) return;
      var scrollTop = menu.scrollTop,
        scrollHeight = menu.scrollHeight,
        clientHeight = menu.clientHeight;
      var atTop = scrollTop === 0;
      var atBottom = scrollTop + clientHeight >= scrollHeight;
      if (atTop && event.deltaY < 0 || atBottom && event.deltaY > 0) {
        event.preventDefault();
      }
    };
    menu.addEventListener("wheel", preventScrollPropagation, {
      passive: false
    });
    menu.addEventListener("touchmove", preventScrollPropagation, {
      passive: false
    });
    return function () {
      menu.removeEventListener("wheel", preventScrollPropagation);
      menu.removeEventListener("touchmove", preventScrollPropagation);
    };
  }, [!!activeMenu]);
  var withinRefs = [triggerRef, menuItemsRef, bodyRef, menuPortalRef];
  // console.log(clearButtonProps);
  React.useEffect(function () {
    if (!activeMenu) {
      return;
    }
    var handleClickOutside = function (event) {
      if (!activeMenu) {
        patchState({
          patch: {
            focusRef: null
          },
          eventName: 'handleClickOutside'
        });
        return;
      }
      if (withinRefs.some(function (ref) {
        var _a;
        return (_a = ref.current) === null || _a === void 0 ? void 0 : _a.contains(event.target);
      })) {
        return; // Click inside, do nothing
      }
      patchState({
        patch: {
          visibleMenuIds: [],
          cursor: 0,
          focusRef: null
        },
        eventName: 'handleClickOutside'
      });
    };
    document.addEventListener("mousedown", handleClickOutside);
    return function () {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [activeMenu, withinRefs]);
  var inputComponentProps = __assign(__assign({}, inputProps), {
    cursorType: inputCursorType,
    colors: colors,
    onClick: showOnInputClick ? handleInputClick : undefined,
    placeholder: isActive ? activePlaceholder || placeholder : placeholder,
    onChange: handleInputChange,
    value: inputValue,
    onKeyDown: handleInputKeyDown,
    onFocus: handleInputFocus
  });
  var handleSelectionsDragStart = function (event) {
    setActiveDragId(event.active.id);
  };
  var handleSelectionsDragEnd = function (event) {
    var active = event.active,
      over = event.over;
    if (!over || active.id === over.id) {
      setActiveDragId(null);
      return;
    }
    var oldIndex = selections.findIndex(function (item) {
      return item.id === active.id;
    });
    var newIndex = selections.findIndex(function (item) {
      return item.id === over.id;
    });
    var newItems = __spreadArray([], __read(selections), false);
    newItems.splice(newIndex, 0, newItems.splice(oldIndex, 1)[0]);
    onSelect({
      items: newItems,
      /* @ts-ignore */
      item: active,
      event: null
    });
    setActiveDragId(null);
  };
  var handleSelectionMenuItemSelect = function (selection) {
    return function (args) {
      // so, here we have the selection this applies to, and the menu item that was selected inside the grip button for that selection
      if (args.item.id === 'remove') {
        // handle select acts as if the item as selected again, which toggles it off
        handleSelect({
          item: selection,
          event: args.event
        });
      } else if (args.item.id === 'start') {
        // move the selection to the start
        var index = selections.findIndex(function (item) {
          return item.id === selection.id;
        });
        var newItems = __spreadArray([], __read(selections), false);
        newItems.splice(index, 1);
        newItems.unshift(selection);
        onSelect({
          items: newItems,
          item: selection,
          event: args.event
        });
      } else if (args.item.id === 'end') {
        // move the selection to the end
        var index = selections.findIndex(function (item) {
          return item.id === selection.id;
        });
        var newItems = __spreadArray([], __read(selections), false);
        newItems.splice(index, 1);
        newItems.push(selection);
        onSelect({
          items: newItems,
          item: selection,
          event: args.event
        });
      }
    };
  };
  var inputContainer = React.createElement(My.InputContainer, {
    gap: ((_f = bodyProps.startProps) === null || _f === void 0 ? void 0 : _f.gap) || 0,
    ref: inputContainerRef
  }, icon, React.createElement(Input, __assign({
    ref: inputRef
  }, inputComponentProps)));
  var menuHeadInputContainer = React.createElement(My.MenuHead, {
    ref: menuHeadContainerRef
  }, React.createElement(My.InputContainer, {
    gap: ((_g = bodyProps.startProps) === null || _g === void 0 ? void 0 : _g.gap) || 0
  }, filterIcon, React.createElement(Input, __assign({
    tabIndex: isActive ? null : -1,
    ref: inputRef
  }, inputComponentProps))), isLoadingInitialItems && React.createElement(My.LoadingMessage, {
    spinnerSize: iconProps.filterSize - 2,
    style: {
      padding: '4px 0px'
    }
  }));
  // common function to handle transtion and non-transition rendering
  var menuElement = function (style) {
    if (style === void 0) {
      style = {};
    }
    return React.createElement(My.Menu, __assign({
      font: font,
      selectedItems: selections,
      ref: menuItemsRef
    }, menuProps, {
      style: __assign(__assign(__assign({}, menuProps.style || {}), positionStyle), style),
      itemsProps: __assign(__assign({}, menuProps === null || menuProps === void 0 ? void 0 : menuProps.itemsProps), {
        maxHeight: maxItemsHeight
      }),
      showHead: true,
      head: !hasRegularInput && menuHeadInputContainer,
      infiniteScroll: infiniteScroll,
      highlightText: inputValue,
      highlightTextClassName: highlightTextClassName,
      onItemSelect: handleSelect,
      // onItemMouseOver={handleItemMouseOver}
      menu: menuToRender,
      activeItem: activeMenuItem,
      key: menu.id
    }));
  };
  var menuElements = useMenuTransitions ? transitions(function (transitionStyle, item) {
    return item && menuElement(transitionStyle);
  }) : activeMenu && menuElement();
  var bodyRect = (_h = bodyRef === null || bodyRef === void 0 ? void 0 : bodyRef.current) === null || _h === void 0 ? void 0 : _h.getBoundingClientRect();
  var portalPosition = {
    top: (bodyRect === null || bodyRect === void 0 ? void 0 : bodyRect.bottom) || 0,
    left: (bodyRect === null || bodyRect === void 0 ? void 0 : bodyRect.left) || 0,
    width: (bodyRect === null || bodyRect === void 0 ? void 0 : bodyRect.width) || 0,
    position: 'fixed'
  };
  var modifiers = useDndDialogModifier({
    ref: bodyRef
  });
  var activeDragItemElement = activeDragItem ? React.createElement(My.Selection, {
    isGhost: true,
    item: activeDragItem
  }, React.createElement(SelectedItemComponent, {
    item: activeDragItem
  })) : null;
  return React.createElement(My.Container, {
    font: font,
    className: cls.name(className),
    highlightTextClassName: highlightTextClassName
  }, (!hasRegularInput || !isLoadingInitialItems) && React.createElement(My.HiddenInput
  // we need to disable the tab index when active, so tabbing behind this control will not re-focus it.
  , {
    // we need to disable the tab index when active, so tabbing behind this control will not re-focus it.
    tabIndex: isActive ? -1 : null,
    ref: hiddenInputRef,
    value: inputValue,
    onChange: handleHiddenInputChange,
    onKeyDown: handleHiddenInputKeyDown,
    onFocus: handleHiddenInputFocus,
    onBlur: handleHiddenInputBlur
  }), React.createElement(My.Body, __assign({
    isActive: bodyIsActive,
    ref: bodyRef,
    onMouseDown: handleBodyMouseDown,
    onClick: handleBodyClick
  }, bodyProps, {
    style: __assign({}, bodyProps.style || {}),
    cursorType: bodyCursorType
  }), React.createElement(My.Control, null, React.createElement(My.Start, __assign({}, bodyProps.startProps), /* this differs to React Select, we move the filter box to the top of the mene, and preserve the selection for a better UX */
  hasSelection ? React.createElement(My.Selections, {
    ref: selectionsRef,
    style: selectionsProps === null || selectionsProps === void 0 ? void 0 : selectionsProps.style,
    onClick: handleSelectionsClick
  }, allowMultipleSelections ? allowSorting ? React.createElement(DndContext, {
    sensors: sensors,
    collisionDetection: closestCenter,
    onDragEnd: handleSelectionsDragEnd,
    onDragStart: handleSelectionsDragStart
  }, React.createElement(DragOverlay, {
    modifiers: modifiers
  }, activeDragItemElement), React.createElement(SortableContext, {
    items: selections
  }, selections.map(function (item) {
    return React.createElement(My.Selection, {
      menuProps: selectionMenuProps,
      menu: selectionMenu,
      item: item,
      onMenuItemSelect: handleSelectionMenuItemSelect(item)
    }, React.createElement(SelectedItemComponent, {
      key: "selection-".concat(item.id),
      item: item
    }));
  }))) : selectionTransitionItems(function (transitionStyle, item) {
    // todo, make a variant with just the remove buttons
    return React.createElement(SelectedItemComponent, {
      style: transitionStyle,
      key: "selection-".concat(item.id),
      item: item
    });
  }) : selections.map(function (item, index) {
    return React.createElement(SelectedItemComponent, {
      key: "selection-".concat(item.id),
      item: item
    });
  })) : hasRegularInput ? inputContainer : PlaceholderComponent ? React.createElement(PlaceholderComponent, __assign({
    placeholder: placeholder,
    label: label
  }, componentProps.placeholderComponentProps)) : icon), showEnd && React.createElement(My.End, __assign({}, bodyProps.endProps), ClearIcon && React.createElement(ClearIcon, __assign({
    onClick: handleClearClick
  }, clearButtonProps)), showMenuIndicatorDivider && MenuIndicatorDivider && React.createElement(MenuIndicatorDivider, __assign({}, menuIndicatorDividerProps)), isLoading ? React.createElement(My.Loader, {
    size: 10,
    color: $(theme.COLOR('subtle'))
  }) : showMenuIndicator && MenuIndicatorIcon && React.createElement(MenuIndicatorIcon, __assign({
    isActive: isActive
  }, omit(menuIndicatorProps, 'component')))))), React.createElement(My.MenuPosition, {
    ref: triggerRef
  }, usePortal ? React.createElement(MenuPortal, {
    ref: menuPortalRef,
    style: portalPosition
  }, menuElements) : menuElements));
};
OptionMenuInput.cls = cls;
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5, templateObject_6, templateObject_7, templateObject_8, templateObject_9, templateObject_10, templateObject_11, templateObject_12, templateObject_13, templateObject_14, templateObject_15, templateObject_16, templateObject_17, templateObject_18, templateObject_19, templateObject_20, templateObject_21, templateObject_22, templateObject_23, templateObject_24, templateObject_25, templateObject_26, templateObject_27, templateObject_28, templateObject_29, templateObject_30, templateObject_31, templateObject_32, templateObject_33, templateObject_34, templateObject_35, templateObject_36, templateObject_37, templateObject_38, templateObject_39, templateObject_40, templateObject_41, templateObject_42, templateObject_43, templateObject_44, templateObject_45;